import React, { useState } from 'react';
import { Loader2, TrendingUp, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TXT = {
  en: {
    kicker: 'ROI ENGINE', h2: 'What is fixing this actually worth to you?',
    sub: 'Enter your real numbers. We combine them with your live scan findings and conservative industry benchmarks — nothing invented.',
    ticket: 'Average ticket ($)', covers: 'Orders / covers per month', delivery: '% of orders via delivery apps (Uber Eats, DoorDash…)',
    cta: 'Calculate my potential', calculating: 'Calculating…',
    perMonth: 'estimated extra revenue / month', perYear: 'per year',
    roi: (x) => `${x}x return vs. the Kitxens plan ($99/mo)`,
    breakdown: 'Where the number comes from — driver by driver',
    strong: 'Your web presence is already strong — we found no major revenue leaks in this scan. The score above tells you where the remaining polish is.',
    note: 'Conservative estimate based on your own inputs, the real findings of your live scan, and published industry benchmarks. Ranges shown on purpose — no one can promise you an exact figure.',
    err: 'Could not calculate. Check your numbers and try again.',
  },
  es: {
    kicker: 'ROI ENGINE', h2: '¿Cuánto vale realmente arreglar esto para ti?',
    sub: 'Introduce tus números reales. Los combinamos con los hallazgos de tu escaneo en vivo y benchmarks conservadores del sector — nada inventado.',
    ticket: 'Ticket medio ($)', covers: 'Pedidos / cubiertos al mes', delivery: '% de pedidos por apps de delivery (Uber Eats, DoorDash…)',
    cta: 'Calcular mi potencial', calculating: 'Calculando…',
    perMonth: 'ingresos extra estimados / mes', perYear: 'al año',
    roi: (x) => `Retorno de ${x}x frente al plan Kitxens ($99/mes)`,
    breakdown: 'De dónde sale el número — driver a driver',
    strong: 'Tu presencia web ya es sólida — no encontramos fugas de ingresos importantes en este escaneo. El score de arriba te dice dónde está el pulido restante.',
    note: 'Estimación conservadora basada en tus propios datos, los hallazgos reales de tu escaneo en vivo y benchmarks publicados del sector. Mostramos rangos a propósito — nadie puede prometerte una cifra exacta.',
    err: 'No se pudo calcular. Revisa tus números e inténtalo de nuevo.',
  },
};

const fmt = (n) => '$' + Number(n).toLocaleString('en-US');

export const RoiEngine = ({ scanId, lang }) => {
  const t = TXT[lang === 'es' ? 'es' : 'en'];
  const L = lang === 'es' ? 'es' : 'en';
  const [form, setForm] = useState({ avg_ticket: '', covers_per_month: '', delivery_share_pct: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [roi, setRoi] = useState(null);

  const calc = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/score/roi`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scan_id: scanId,
          avg_ticket: parseFloat(form.avg_ticket),
          covers_per_month: parseInt(form.covers_per_month, 10),
          delivery_share_pct: parseFloat(form.delivery_share_pct || '0'),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'roi failed');
      setRoi(d);
    } catch (err) {
      setError(typeof err.message === 'string' && err.message.length < 200 ? err.message : t.err);
    } finally { setBusy(false); }
  };

  const inputCls = 'w-full h-12 rounded-lg bg-white/[0.05] border border-white/15 px-4 text-[15px] text-white placeholder-neutral-500 focus:outline-none focus:border-[#FF9B4A]/70 transition-colors';

  return (
    <div className="mt-12 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 lg:p-8" data-testid="roi-engine">
      <p className="text-[#FF9B4A] text-xs font-bold tracking-[3px] flex items-center gap-2"><Calculator className="w-4 h-4" /> {t.kicker}</p>
      <h2 className="mt-2 text-lg md:text-xl font-bold text-white">{t.h2}</h2>
      <p className="mt-1.5 text-[13.5px] text-neutral-400 max-w-2xl">{t.sub}</p>

      <form onSubmit={calc} className="mt-5 grid sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[12px] text-neutral-400 font-semibold">{t.ticket}</label>
          <input required type="number" min="1" step="0.5" data-testid="roi-ticket-input" className={`${inputCls} mt-1`}
            placeholder="28" value={form.avg_ticket} onChange={(e) => setForm({ ...form, avg_ticket: e.target.value })} />
        </div>
        <div>
          <label className="text-[12px] text-neutral-400 font-semibold">{t.covers}</label>
          <input required type="number" min="1" data-testid="roi-covers-input" className={`${inputCls} mt-1`}
            placeholder="1200" value={form.covers_per_month} onChange={(e) => setForm({ ...form, covers_per_month: e.target.value })} />
        </div>
        <div>
          <label className="text-[12px] text-neutral-400 font-semibold">{t.delivery}</label>
          <input type="number" min="0" max="100" data-testid="roi-delivery-input" className={`${inputCls} mt-1`}
            placeholder="20" value={form.delivery_share_pct} onChange={(e) => setForm({ ...form, delivery_share_pct: e.target.value })} />
        </div>
      </form>
      {error && <p className="mt-2 text-red-400 text-[13px]" data-testid="roi-error">{error}</p>}
      <button onClick={calc} disabled={busy} data-testid="roi-calc-btn"
        className="mt-4 h-12 px-7 rounded-full bg-[#F77F11] hover:bg-[#d8651a] text-white font-bold text-[14px] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.calculating}</> : <><TrendingUp className="w-4 h-4" /> {t.cta}</>}
      </button>

      {roi && (
        <div className="mt-8" data-testid="roi-result">
          {roi.drivers.length === 0 ? (
            <p className="text-[14px] text-emerald-300 font-semibold">{t.strong}</p>
          ) : (
            <>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-4xl sm:text-5xl font-bold text-white tabular-nums" data-testid="roi-monthly-range">
                  {fmt(roi.monthly_low)} – {fmt(roi.monthly_high)}
                </span>
                <span className="text-[13.5px] text-neutral-400">{t.perMonth}</span>
              </div>
              <p className="mt-1 text-[14px] text-neutral-300">
                <span className="font-bold text-[#FF9B4A]">{fmt(roi.annual_low)} – {fmt(roi.annual_high)}</span> {t.perYear}
                {roi.roi_multiple > 0 && <span className="ml-3 text-[12.5px] font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300" data-testid="roi-multiple">{t.roi(roi.roi_multiple)}</span>}
              </p>
              <p className="mt-6 text-[13px] font-bold text-white">{t.breakdown}</p>
              <div className="mt-3 space-y-2.5">
                {roi.drivers.map((d) => (
                  <div key={d.id} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4" data-testid={`roi-driver-${d.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-[13.5px] font-semibold text-white">{d.label[L] || d.label.en}</p>
                      <p className="text-[13.5px] font-bold text-[#FF9B4A] tabular-nums">{fmt(d.monthly_low)} – {fmt(d.monthly_high)}/mo</p>
                    </div>
                    <p className="mt-1 text-[12.5px] text-neutral-500 leading-relaxed">{d.basis[L] || d.basis.en}</p>
                  </div>
                ))}
              </div>
            </>
          )}
          <p className="mt-5 text-[11.5px] text-neutral-600 leading-relaxed max-w-2xl">{t.note}</p>
        </div>
      )}
    </div>
  );
};
